import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { PhotographyAction } from '@stores/photography/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps, BasePageState } from '../../../../types/base';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { BASE_IMG_URL, livePhotoImage, photographyImage } from '@images/imageUrls';
import updateImageListSrc from '@utils/update-image-list-src';
import Toast from '@components/Toast';
import Avatar from '@components/avatar';
import { px2rem } from '@utils/px2rem';
import { protectedMediaStyle } from '@utils/contentProtection';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import CommonSharePopup from "@components/commonSharePopup";
import PhotographyExhibitionViewer from '@components/photographyExhibitionViewer';
import PhotographyMediaIcon from '@components/photographyMediaIcon';
import { isVideoWork } from '@utils/photographyVideo';

const LIVE_ICON_URL = livePhotoImage.liveIcon;
const SOUND_CLOSE_ICON_URL = livePhotoImage.volumeClose;
const SOUND_OPEN_ICON_URL = livePhotoImage.volume;

interface RouteParams {
    id: string;
}

interface TabItem {
    name: string;
    id: number;
    count?: number;
}

interface PhotoVideoInfo {
    url?: string;
    videoUrl?: string;
}

interface PhotoItem {
    id: number;
    number?: number;
    name: string;
    image: string;
    width?: number;
    height?: number;
    voteCount?: number;
    createdAt?: string;
    nickname?: string;
    avatar?: string;
    user?: { titleMark?: number | string; level?: number | string };
    code?: string;
    type?: string;
    video?: string | PhotoVideoInfo;
}

interface PhotoExhibitionH5Props extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    photography?: PhotographyAction;
}

interface PhotoExhibitionH5State extends BasePageState {
    photographyDetail: any;
    photographyDetailData: any
    tabList: TabItem[];
    currentTab: number;
    photoList: PhotoItem[];
    currentPhotoIndex: number;
    previewVisible: boolean;
    isTabBarFixed: boolean;
    swiperInitialized: boolean;
    sharePopupStatus: boolean
    currentLivePlaying: boolean;
    currentLiveMuted: boolean;
    isVivoBrowserStatus: boolean
}

@inject('app', 'site', 'user', 'photography')
@observer
class PhotoExhibitionH5Page extends BasePage<PhotoExhibitionH5Props, PhotoExhibitionH5State> {
    photographyId: any = '';
    thumbListRef: React.RefObject<HTMLDivElement> = React.createRef();
    thumbListBoxRef: React.RefObject<HTMLDivElement> = React.createRef();
    tabBarStickyRef: React.RefObject<HTMLDivElement> = React.createRef();
    private liveVideoRef: HTMLVideoElement | null = null;
    private docContextMenuHandler: ((e: MouseEvent) => void) | null = null;

    state: PhotoExhibitionH5State = {
        loading: false,
        error: null,
        pageLoaded: false,
        photographyDetailData: {},  // 大赛详情
        photographyDetail: {},
        tabList: [],
        currentTab: 0,
        photoList: [],
        currentPhotoIndex: 0,
        previewVisible: false,
        isTabBarFixed: false,
        swiperInitialized: false,
        sharePopupStatus: false,    // 分享弹框是否显示
        currentLivePlaying: false,
        currentLiveMuted: true,
        isVivoBrowserStatus: false, // 是否是vivo浏览器，如果是vivo浏览器，屏蔽掉live图的播放
    };
    private swiperRef: React.RefObject<any> = React.createRef();

    componentDidMount() {
        const { site, match } = this.props;
        this.photographyId = parseInt(match.params.id);
        this.getPhotoDetailData();
        if (site) {
            site.setShowHeader(false);
        }
        window.addEventListener('scroll', this.handleWindowScroll, { passive: true });
        this.handleWindowScroll();
        super.componentDidMount();
        // 禁止整个页面右键/长按
        this.docContextMenuHandler = (e: MouseEvent) => e.preventDefault();
        document.addEventListener('contextmenu', this.docContextMenuHandler);
        // 判断是不是vivo浏览器
        this.isVivoBrowser();
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleWindowScroll);
        this.pauseCurrentLive(false);
        // 移除右键禁用
        if (this.docContextMenuHandler) {
            document.removeEventListener('contextmenu', this.docContextMenuHandler);
            this.docContextMenuHandler = null;
        }
    }

    /**
     * 判断当前浏览器是否为 vivo 浏览器
     * @returns {boolean} true: 是 vivo 浏览器, false: 不是
     */
    isVivoBrowser = () => {
        if (typeof navigator === 'undefined') {
            return false;
        }
        
        const ua = navigator.userAgent.toLowerCase();
        // 优先判断 VivoBrowser 关键词，其次判断 vivo 关键词作为兼容
        let isVivoBrowser = /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            this.setState({
                isVivoBrowserStatus: isVivoBrowser
            })
        return /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            
    }

    // 获取影展详情
    getPhotoDetailData = () => {
        const { photography } = this.props;
        if (photography) {
            photography.getPhotographyExhibitions(this.photographyId).then((res: any) => {
                const exhibition = res?.data || {};
                const categoryList = exhibition?.categories || [];
                const currentCategory = categoryList[0] || {};
                const works = currentCategory?.works || [];
                const nextPhotoList = works.map((item: any) => ({
                    ...item,
                    nickname: item.user?.nickname,
                    avatar: item.user?.avatar,
                    code: item.user?.code,
                }));
                this.getPhotoDetail(exhibition?.contestCode);
                this.setState({
                    photographyDetail: exhibition,
                    tabList: categoryList.map((item: any) => ({
                        name: item.name,
                        id: item.id,
                        count: item.works?.length || 0,
                    })),
                    photoList: nextPhotoList,
                    currentTab: 0,
                    currentPhotoIndex: 0,
                    currentLivePlaying: false,
                    currentLiveMuted: true,
                }, () => {
                    this.maybeAutoPlayCurrentLive(nextPhotoList[0]);
                });
            });
        }
    };
    // 获取大赛详情(用于分享的时候使用大赛的名称)
    getPhotoDetail = (code) => {  
        const { photography } = this.props;
        if(photography) {
            photography.getPhotographyContestsDetail(code).then(res => {
                if(res.code == 0) {
                    this.setState({
                        photographyDetailData: res.data,
                    })
                } else {
                    // Toast.info('服务器错误，请稍后重试')
                }
            });
        }
    }

    // Tab 切换
    handleTabClick = (index: number) => {
        this.pauseCurrentLive(false);
        const categories = this.state.photographyDetail?.categories || [];
        const currentCategory = categories[index] || {};
        const works = currentCategory?.works || [];
        const nextPhotoList = works.map((item: any) => ({
            ...item,
            nickname: item.user?.nickname,
            avatar: item.user?.avatar,
            code: item.user?.code,
        }));

        this.setState({
            currentTab: index,
            currentPhotoIndex: 0,
            photoList: nextPhotoList,
            currentLivePlaying: false,
            currentLiveMuted: true,
        }, () => {
            if (this.swiperRef.current?.swiper) {
                this.swiperRef.current.swiper.slideTo(0, 0);
            }
            this.maybeAutoPlayCurrentLive(nextPhotoList[0]);
        });
    };

    // 切换到指定照片（使用Swiper实例）
    goToPhoto = (index: number) => {
        const { photoList } = this.state;
        if (index < 0 || index >= photoList.length) return;
        this.pauseCurrentLive(false);
        if (this.swiperRef.current && this.swiperRef.current.swiper) {
            this.swiperRef.current.swiper.slideTo(index);
        } else {
            const targetPhoto = photoList[index];
            this.setState({
                currentPhotoIndex: index,
                currentLivePlaying: false,
                currentLiveMuted: true,
            }, () => {
                this.maybeAutoPlayCurrentLive(targetPhoto);
            });
        }
    };

    // Swiper滑动事件
    handleSlideChange = (swiper: any) => {
        this.pauseCurrentLive(false);
        const targetPhoto = this.state.photoList[swiper.activeIndex];
        // 首次滑动时标记为已初始化
        if (!this.state.swiperInitialized) {
            this.setState({ swiperInitialized: true });
        }
        this.setState({
            currentPhotoIndex: swiper.activeIndex,
            currentLivePlaying: false,
            currentLiveMuted: true,
        }, () => {
            this.scrollThumbIntoView(swiper.activeIndex);
            this.maybeAutoPlayCurrentLive(targetPhoto);
        });
    };

    // Swiper到达边界事件
    handleReachBeginning = () => {
        if (!this.state.swiperInitialized || this.state.previewVisible) return;
        Toast.info({ content: '已是第一张图片' });
    };

    handleReachEnd = () => {
        if (!this.state.swiperInitialized || this.state.previewVisible) return;
        Toast.info({ content: '已是最后一张图片' });
    };

    // 自动滚动缩略图使选中项居中显示
    scrollThumbIntoView = (index: number) => {
        const container = this.thumbListBoxRef.current;
        const list = this.thumbListRef.current;
        if (!container || !list) return;
        const child = list.children[index] as HTMLElement;
        if (!child) return;
        // 计算子元素相对于容器的位置
        const childLeft = child.offsetLeft;
        const childWidth = child.clientWidth;
        const containerWidth = container.clientWidth;
        // 滚动目标位置：让子元素居中
        const scrollTarget = childLeft - (containerWidth / 2) + (childWidth / 2);
        container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    };

    handleWindowScroll = () => {
        const el = this.tabBarStickyRef.current;
        if (!el) return;
        const { isTabBarFixed } = this.state;
        const rect = el.getBoundingClientRect();
        const shouldFixed = isTabBarFixed ? rect.top < 0 : rect.top <= 0;
        if (shouldFixed !== isTabBarFixed) {
            this.setState({ isTabBarFixed: shouldFixed });
        }
    };

    handlePreviewOpen = (index: number) => {
        if (!this.state.photoList[index]) return;
        this.pauseCurrentLive();
        this.goToPhoto(index);
        this.setState({
            previewVisible: true,
        });
    };

    handlePreviewClose = (index: number) => {
        this.goToPhoto(index);
        this.setState({ previewVisible: false });
    };

    getCurrentPhoto = () => {
        return this.state.photoList[this.state.currentPhotoIndex] || null;
    }

    isLivePhoto = (work = this.getCurrentPhoto()) => {
        return String(work?.type || '').toLowerCase() === 'live';
    }

    getCurrentLiveUrl = (work = this.getCurrentPhoto()) => {
        if (!work?.video) {
            return '';
        }
        if (typeof work.video === 'string') {
            return work.video;
        }
        return work.video.url || work.video.videoUrl || '';
    }

    pauseCurrentLive = (shouldUpdateState = true) => {
        if (this.liveVideoRef) {
            this.liveVideoRef.pause();
            this.liveVideoRef.currentTime = 0;
        }
        if (shouldUpdateState) {
            this.setState({ currentLivePlaying: false });
        }
    }

    playCurrentLive = () => {
        const liveUrl = this.getCurrentLiveUrl();
        if (!liveUrl || !this.liveVideoRef) {
            return;
        }
        this.liveVideoRef.currentTime = 0;
        this.liveVideoRef.muted = this.state.currentLiveMuted;
        this.liveVideoRef.play().then(() => {
            this.setState({ currentLivePlaying: true });
        }).catch(() => {});
    }

    handleCurrentLiveClick = () => {
        if (!this.getCurrentLiveUrl()) {
            return;
        }
        if (this.state.currentLivePlaying) {
            this.pauseCurrentLive();
            return;
        }
        this.playCurrentLive();
    }

    handleCurrentLiveEnded = () => {
        this.setState({ currentLivePlaying: false });
    }

    maybeAutoPlayCurrentLive = (work?: PhotoItem | null) => {
        if (!this.isLivePhoto(work) || !this.getCurrentLiveUrl(work)) {
            return;
        }
        window.setTimeout(() => {
            const currentPhoto = this.getCurrentPhoto();
            if (!this.state.previewVisible && !this.state.sharePopupStatus && currentPhoto?.id === work?.id && this.isLivePhoto(currentPhoto)) {
                this.playCurrentLive();
            }
        }, 80);
    }

    toggleCurrentLiveMute = (e: React.MouseEvent) => {
        e.stopPropagation();
        const nextMuted = !this.state.currentLiveMuted;
        this.setState({ currentLiveMuted: nextMuted });
        if (this.liveVideoRef) {
            this.liveVideoRef.muted = nextMuted;
        }
    }

    // 打开app查看更多大赛
    handleOpenApp = () => {
        window.location.href = 'https://m.malink.cn/s/ABbE73';
    };

    // 关闭弹框
    onHidePopup = () => {
        console.log('关闭');
        this.setState({
            sharePopupStatus: false,
        })
    }

    render() {
        const {
            photographyDetail,
            tabList,
            currentTab,
            photoList,
            currentPhotoIndex,
            previewVisible,
            sharePopupStatus,
            photographyDetailData,
            currentLivePlaying,
            currentLiveMuted,
            isVivoBrowserStatus
        } = this.state;
        const { site } = this.props;
        const downloadAppStatus = site?.appDownloadShowStatus ?? false;

        const bannerImage = photographyDetail?.cover || '';
        const bannerText = photographyDetail?.description || photographyDetail?.name || '';
        const totalCount = photoList.length;
        const isProdEnv = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
        return (
            <div className={styles.wrapper} style={{ paddingTop: downloadAppStatus ? px2rem(100) : 0 }}>
                {/* ===== 顶部 Banner ===== */}
                <div className={styles.bannerSection}>
                    <div className={styles.backBtn} onClick={() => {window.ensureBack(() => {});}}>
                        <img src={`${BASE_IMG_URL}/photography/home2.png`} alt="" />
                    </div>
                    <div className={styles.shareBtn} onClick={() => {
                        this.setState({
                            sharePopupStatus: true
                        })
                    }}>
                        <img src={`${BASE_IMG_URL}/photography/share.png`} alt="" />
                    </div>
                    <img
                        src={updateImageListSrc(bannerImage, 20)}
                        alt=""
                        className={styles.bannerImg}
                    />
                    <img src={photographyImage.modelMask} alt="" className={styles.bannerMask} />
                    <div className={styles.bannerText}>
                        {bannerText}
                    </div>
                </div>

                {/* ===== Tab 栏 ===== */}
                <div
                    className={`${styles.tabBarSticky} ${this.state.isTabBarFixed ? styles.tabBarStickyFixed : ''}`}
                    ref={this.tabBarStickyRef}
                >
                    <div className={styles.tabBar} style={{ top: this.state.isTabBarFixed && downloadAppStatus ? px2rem(88) : undefined }}>
                        {tabList.map((tab, index) => (
                            <div
                                key={tab.id}
                                className={`${styles.tabItem} ${currentTab === index ? styles.tabItemActive : ''}`}
                                onClick={() => this.handleTabClick(index)}
                            >
                                {tab.name}{currentTab === index && tab.count !== undefined ? `(${tab.count})` : ''}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ===== 作品轮播 ===== */}
                <div className={styles.swiperSection}>
                    <Swiper
                        key={`swiper-${photoList.length}`}
                        modules={[EffectCoverflow]}
                        ref={this.swiperRef}
                        effect={'coverflow'}
                        grabCursor={true}
                        centeredSlides={true}
                        slidesPerView={'auto'}
                        initialSlide={currentPhotoIndex}
                        coverflowEffect={{
                            rotate: 34,
                            stretch: 0,
                            depth: 120,
                            modifier: 1,
                            slideShadows: false,
                        }}
                        onSlideChange={this.handleSlideChange}
                        onReachBeginning={this.handleReachBeginning}
                        onReachEnd={this.handleReachEnd}
                        className={styles.photoSwiper}
                    >
                        {photoList.map((photo, index) => {
                            const isLandscape = !!photo.width && !!photo.height && photo.width > photo.height;
                            const isCurrentSlide = currentPhotoIndex === index;
                            const isLivePhoto = this.isLivePhoto(photo);
                            const liveUrl = this.getCurrentLiveUrl(photo);

                            return (
                                <SwiperSlide key={photo.id}>
                                    <div className={`${styles.slideInner} ${isVideoWork(photo) ? styles.videoSlide : ''}`}>
                                        <div className={`${styles.cardWrap} ${isLandscape ? styles.cardWrapLandscape : ''}`}>
                                            {isLivePhoto && <div className={styles.liveTagBar}>
                                                <div className={styles.liveTag} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); if (isCurrentSlide) { this.playCurrentLive(); } }}>
                                                    <img className={styles.liveIcon} src={LIVE_ICON_URL} alt="" />
                                                    <span>动态照片</span>
                                                </div>
                                                {isCurrentSlide && <div className={styles.soundBtn} onClick={this.toggleCurrentLiveMute}>
                                                    <img className={styles.soundIcon} src={currentLiveMuted ? SOUND_CLOSE_ICON_URL : SOUND_OPEN_ICON_URL} alt="" />
                                                </div>}
                                            </div>}
                                            <div className={styles.cardContent}>
                                                <div className={`${styles.photoBox} ${isVideoWork(photo) ? styles.videoBox : ''}`} style={isVideoWork(photo) ? { aspectRatio: photo.width && photo.height ? `${photo.width} / ${photo.height}` : '9 / 16' } : undefined} onClick={() => {
                                                    if (isCurrentSlide && isLivePhoto) {
                                                        this.handleCurrentLiveClick();
                                                        return;
                                                    }
                                                    this.handlePreviewOpen(index);
                                                }}>
                                                    {isVideoWork(photo) ? <>
                                                        <img src={updateImageListSrc(photo.image, 10)} alt={photo.name} className={styles.videoCover}
                                                            style={protectedMediaStyle} onContextMenu={event => event.preventDefault()} />
                                                        <button type="button" className={styles.videoPlay} aria-label={`播放${photo.name}`}
                                                            onClick={event => { event.stopPropagation(); this.handlePreviewOpen(index); }}>
                                                            <PhotographyMediaIcon kind="play" disc />
                                                        </button>
                                                    </> : isCurrentSlide && isLivePhoto && liveUrl && !isVivoBrowserStatus && !previewVisible && !sharePopupStatus ? (
                                                        <div className={styles.liveMediaBox}>
                                                            <video
                                                                ref={(el) => { this.liveVideoRef = el; }}
                                                                src={liveUrl}
                                                                className={styles.liveVideo}
                                                                playsInline
                                                                muted={currentLiveMuted}
                                                                poster={updateImageListSrc(photo.image, 10)}
                                                                onEnded={this.handleCurrentLiveEnded}
                                                                style={protectedMediaStyle}
                                                                onContextMenu={(e) => e.preventDefault()}
                                                            />
                                                            {!currentLivePlaying && (
                                                                <img
                                                                    src={updateImageListSrc(photo.image, 10)}
                                                                    alt={photo.name}
                                                                    className={styles.liveCoverImage}
                                                                    style={protectedMediaStyle}
                                                                    onContextMenu={(e) => e.preventDefault()}
                                                                />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <img src={updateImageListSrc(photo.image, 10)} alt={photo.name} className={styles.photoImg} style={protectedMediaStyle} onContextMenu={(e) => e.preventDefault()} />
                                                    )}
                                                </div>
                                                <div className={`${styles.cardInfo} ${isLandscape ? styles.cardInfoLandscape : ''}`}>
                                                    <div className={styles.photoTitle}>《{photo.name}》</div>
                                                    <div className={styles.authorInfo}>
                                                        {!!photo.avatar && (
                                                            <Avatar
                                                                avatarUrl={photo.avatar}
                                                                userCode={photo?.code}
                                                                size={32}
                                                                titleMark={photo.user?.titleMark}
                                                                level={photo.user?.level}
                                                            ></Avatar>
                                                        )}
                                                        {!!photo.nickname && (
                                                            <span className={styles.authorName}>{photo.nickname}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>

                    {/* 缩略图列表 */}
                    <div className={styles.thumbListOutBox}>      
                        <div className={styles.thumbListBox} ref={this.thumbListBoxRef}>
                            <div className={styles.thumbList} ref={this.thumbListRef}>
                                {photoList.map((photo, index) => (
                                    <img
                                        key={photo.id}
                                        src={photo.image}
                                        alt=""
                                        className={`${styles.thumbItem} ${currentPhotoIndex === index ? styles.thumbItemActive : ''}`}
                                        onClick={() => this.goToPhoto(index)}
                                        style={protectedMediaStyle}
                                        onContextMenu={(e) => e.preventDefault()}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 计数 */}
                    <div className={styles.counter}>
                        {totalCount > 0 ? `${currentPhotoIndex + 1}/${totalCount}` : '0/0'}
                    </div>

                    {/* 查看更多大赛 */}
                    <div className={styles.moreBtn} onClick={this.handleOpenApp}>打开App端查看更多大赛</div>
                </div>

                {previewVisible && <PhotographyExhibitionViewer
                    works={photoList}
                    initialIndex={currentPhotoIndex}
                    onClose={this.handlePreviewClose}
                />}
                {/* 分享弹框 */}
                <CommonSharePopup
                    visible={sharePopupStatus}
                    title="分享"
                    copyText={isProdEnv ? `vivo社区粉丝摄影大赛「${photographyDetailData?.name}」影展出炉啦！\r\nhttps://club.vivo.com.cn/photoExhibition/${photographyDetail.id} \r\n复制本消息打开vivo社区App即刻欣赏~` : `vivo社区粉丝摄影大赛「${photographyDetailData?.name}」影展出炉啦！\r\nhttps://club.vivo.com.cn/photoExhibition/${photographyDetail.id} \r\n复制本消息打开vivo社区App即刻欣赏~`}
                    copyToastText='链接复制成功，快去分享好友吧～'
                    copyLink={() => this.onHidePopup()}
                    onCancelChange={() => this.onHidePopup()}
                >
                </CommonSharePopup>
            </div>
        );
    }
}

export default withRouter(PhotoExhibitionH5Page);
